import dayjs, { Dayjs } from 'dayjs'
import PrintQueueEntry from '../../../../types/api/print-queue-entry'
import { buildPrintQueueEntryTimings, PrintQueueEntryTiming } from './build-print-queue-end-times'

function groupEntriesByMachine(entries: PrintQueueEntry[]) {
    const map = new Map<string, PrintQueueEntry[]>()

    entries.forEach(entry => {
        const machineDocId = entry.machine?.documentId
        if (!machineDocId) {
            return
        }

        const key = String(machineDocId)
        if (!map.has(key)) {
            map.set(key, [])
        }
        map.get(key)!.push(entry)
    })

    return map
}

function buildTimingMap(entries: PrintQueueEntry[], now: Dayjs) {
    const timings = new Map<string, PrintQueueEntryTiming>()
    const machineEntriesMap = groupEntriesByMachine(entries)

    machineEntriesMap.forEach(machineEntries => {
        const sortedMachineEntries = [...machineEntries].sort((a, b) => a.sort - b.sort)
        const machineTimings = buildPrintQueueEntryTimings(sortedMachineEntries, now)
        machineTimings.forEach((timing, entryDocId) => {
            timings.set(entryDocId, timing)
        })
    })

    return timings
}

function getPrintJobKey(entry: PrintQueueEntry) {
    return entry.print_job?.documentId || String(entry.print_job?.id || '')
}

function getMachineLabel(entry: PrintQueueEntry) {
    return entry.machine?.name || entry.machine?.machine_brand?.name || 'станок'
}

function buildConflictMessage(
    currentEntry: PrintQueueEntry,
    previousEntry: PrintQueueEntry,
    currentStartAt: Dayjs,
    previousEndAt: Dayjs
) {
    const nextStep = currentEntry.executionOrder
    const prevStep = previousEntry.executionOrder
    const nextMachine = getMachineLabel(currentEntry)
    const prevMachine = getMachineLabel(previousEntry)

    return `Шаг ${nextStep} (${nextMachine}) начнётся ~${currentStartAt.format('DD.MM HH:mm')}, а шаг ${prevStep} (${prevMachine}) закончится ~${previousEndAt.format('DD.MM HH:mm')}. Сдвиньте задания в очереди ${nextMachine} или переставьте порядок.`
}

export function buildPrintQueueExecutionOrderConflicts(
    entries: PrintQueueEntry[],
    now: Dayjs = dayjs()
) {
    const conflicts = new Map<string, string>()
    const timingMap = buildTimingMap(entries, now)
    const groupedByPrintJob = new Map<string, PrintQueueEntry[]>()

    entries.forEach(entry => {
        const jobKey = getPrintJobKey(entry)
        if (!jobKey) {
            return
        }

        if (!groupedByPrintJob.has(jobKey)) {
            groupedByPrintJob.set(jobKey, [])
        }

        groupedByPrintJob.get(jobKey)!.push(entry)
    })

    groupedByPrintJob.forEach(jobEntries => {
        const orderedEntries = jobEntries
            .filter(entry => typeof entry.executionOrder === 'number')
            .sort((a, b) => (a.executionOrder || 0) - (b.executionOrder || 0))

        for (let index = 1; index < orderedEntries.length; index += 1) {
            const previousEntry = orderedEntries[index - 1]
            const currentEntry = orderedEntries[index]
            const previousTiming = timingMap.get(previousEntry.documentId)
            const currentTiming = timingMap.get(currentEntry.documentId)

            if (!previousTiming || !currentTiming) {
                continue
            }

            const currentStartAt = dayjs(currentTiming.startAt)
            const previousEndAt = dayjs(previousTiming.endAt)

            if (currentStartAt.isBefore(previousEndAt)) {
                conflicts.set(
                    currentEntry.documentId,
                    buildConflictMessage(currentEntry, previousEntry, currentStartAt, previousEndAt)
                )
            }
        }
    })

    return conflicts
}
